import React from 'react';

import {
	LoadingContainer,
	Wrapper,
	Header,
	RightSection,
	RangeWrapper,
	InputWrapper,
	SpendWrapper,
	SearchWrapper,
	NoData,
	Divider,
	DropdownWrapper,
	AbsoluteWrapper,
} from './ItemsSelectionStep.styles';
import {
	ApLoaderDefault,
	ApLoaderText,
	ApSimpleDropdown,
	ApInput,
	ApToggleSwitch,
} from '@alixpartners/ui-components';
import {
	groupByLevel,
	getLeaves,
	filterGrouped,
	aggregateLeavesByName,
	ITEMS_TYPE,
	GROUP_OPTIONS,
} from '../../utils';
import { ItemsTable } from '../ItemsTable';
import {
	getIdealDenomination,
	UNIT_MAP,
	DENOMINATION_UNITS,
} from 'utils/number';
import Slider from 'rc-slider';
import { SearchInput } from 'components/SearchInput';
import sortBy from 'lodash/sortBy';
import isEqual from 'lodash/isEqual';

export class ItemsSelectionStep extends React.Component {
	state = {
		spendRange: [0, 0],
		minMax: [0, 0],
		unit: '',
		searchItem: null,
		tailSpendItems: this.getDefaultTailSpendItems(),
	};

	componentDidMount() {
		this.updateDefaultSpendRange();
		this.updateItemsOnEdit();
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.clearSearchItem);
	}

	componentDidUpdate(prevProps, prevState) {
		const { data } = this.props;
		const { searchItem, tailSpendItems } = this.state;

		if (data && data !== prevProps.data) {
			this.updateDefaultSpendRange();
			this.updateItemsOnEdit();
		}

		if (searchItem !== prevState.searchItem) {
			if (searchItem) {
				document.addEventListener('click', this.clearSearchItem);
			} else {
				document.removeEventListener('click', this.clearSearchItem);
			}
		}

		if (!isEqual(tailSpendItems, prevState.tailSpendItems)) {
			this.updateItemsTailSpend();
		}
	}

	updateItemsOnEdit = () => {
		const { initiativeData, data, onInitiativeChange } = this.props;
		const { items, initiativeItemType } = initiativeData;

		if (
			!data ||
			!items ||
			items.length === 0 ||
			initiativeItemType !== ITEMS_TYPE.CATEGORY
		) {
			return;
		}

		const allDataNodes = getLeaves(data);
		const updatedItems = items.map(item => {
			const originalItem = allDataNodes.find(
				node => node.name === item.name && node.groupName === item.groupName
			);

			if (!originalItem) {
				return item;
			}

			return {
				...item,
				headSpend: originalItem.headSpend,
				tailSpend: originalItem.tailSpend,
			};
		});

		onInitiativeChange({ items: updatedItems });
	};

	getDefaultTailSpendItems() {
		const { initiativeData } = this.props;
		const { items } = initiativeData;

		if (items && items.length > 0) {
			return items.filter(item => !item.includeTailSpend);
		}

		return [];
	}

	updateItemsTailSpend = () => {
		const { onInitiativeChange, initiativeData } = this.props;
		const { items } = initiativeData;

		if (!items || items.length === 0) {
			return;
		}

		const { tailSpendItems } = this.state;
		const updatedItems = items.map(item => ({
			...item,
			includeTailSpend: !tailSpendItems.some(
				tailItem =>
					tailItem.name === item.name && tailItem.groupName === item.groupName
			),
		}));

		onInitiativeChange({ items: updatedItems });
	};

	updateDefaultSpendRange() {
		const { data } = this.props;

		if (!data) {
			return;
		}

		const maxValue = Math.max.apply(
			null,
			getLeaves(data).map(item => item.spend)
		);
		const denomination = getIdealDenomination(maxValue);
		const formattedMaxValue = Math.ceil((maxValue * 2) / denomination);
		const minValue = Math.min.apply(
			null,
			getLeaves(data).map(item => item.spend)
		);
		const minDenomination = getIdealDenomination(minValue);
		const formattedMinValue =
			minValue > 0 ? 0 : Math.ceil((minValue * 2) / minDenomination);
		// Negative values are filtered out because of min set to zero. Set min according to data if needed.
		this.setState({
			spendRange: [formattedMinValue, formattedMaxValue],
			unit: DENOMINATION_UNITS[denomination],
			minMax: [formattedMinValue, formattedMaxValue],
		});
	}

	getFilteredData = () => {
		const {
			data,
			groupBy,
			filterCategory,
			initiativeData,
			isCommonVendors,
		} = this.props;
		const { initiativeItemType } = initiativeData;
		const { spendRange, unit, tailSpendItems } = this.state;

		const filteredByCategory = filterCategory.value
			? data.filter(({ id }) => id === filterCategory.value)
			: data;
		const grouped = groupByLevel(filteredByCategory, groupBy.value);
		const spendFilterFunc = node => {
			return (
				node.spend >= spendRange[0] * UNIT_MAP[unit] &&
				node.spend <= spendRange[1] * UNIT_MAP[unit]
			);
		};

		if (initiativeItemType === ITEMS_TYPE.VENDOR) {
			let filterFunc = spendFilterFunc;

			// Filter common vendors
			if (isCommonVendors) {
				// Check for common in aggregated data, not in group
				const aggregatedVendors = aggregateLeavesByName(getLeaves(data));
				const isCommonFunc = node => {
					const aggregatedVendor = aggregatedVendors.find(
						item => item.name === node.name
					);

					return (
						aggregatedVendor &&
						aggregatedVendor.companies &&
						Object.keys(aggregatedVendor.companies).length > 1
					);
				};

				filterFunc = node => {
					return isCommonFunc(node) && spendFilterFunc(node);
				};
			}

			// Aggregate Vendors leaves
			return filterGrouped(aggregateLeavesByName(grouped), filterFunc);
		}

		let filtered = filterGrouped(grouped, spendFilterFunc);

		if (initiativeItemType === ITEMS_TYPE.CATEGORY) {
			filtered = filtered.map(item => {
				const isGroup = item.children && item.children.length > 0;
				const isTailSpendIncluded = node =>
					!tailSpendItems.some(
						tailItem =>
							tailItem.name === node.name &&
							tailItem.groupName === node.groupName
					);

				return {
					...item,
					includeTailSpend: !isGroup
						? isTailSpendIncluded(item)
						: item.children.every(isTailSpendIncluded),
					...(isGroup
						? {
								children: item.children.map(child => ({
									...child,
									includeTailSpend: isTailSpendIncluded(child),
								})),
						  }
						: {}),
				};
			});
		}

		return filtered;
	};

	handleSpendRangeChange = spendRange => {
		this.setState({ spendRange });
	};

	handleRangeLowerChange = value => {
		const { spendRange } = this.state;

		let number = Number(value);

		if (number > spendRange[1]) {
			number = spendRange[1];
		}

		// if (number < spendRange[0]) {
		// 	number = spendRange[0];
		// }

		this.setState({ spendRange: [number, spendRange[1]] });
	};

	handleRangeUpperChange = value => {
		const { spendRange } = this.state;
		let number = Number(value);

		if (number < spendRange[0]) {
			number = spendRange[0];
		}

		this.setState({ spendRange: [spendRange[0], number] });
	};

	selectSearchItem = searchItem => {
		this.setState({
			searchItem,
		});
	};

	clearSearchItem = () => {
		this.setState({
			searchItem: null,
		});
	};

	getSearchOptions = filteredData => {
		const { data, spendFormatter } = this.props;

		if (!data) {
			return [];
		}

		return sortBy(
			getLeaves(filteredData).map(({ name, id, groupName, spend }) => ({
				name,
				groupName,
				value: name,
				id,
				info: spendFormatter(spend),
			})),
			'name'
		);
	};

	handleItemsChange = items => {
		const { onInitiativeChange } = this.props;

		onInitiativeChange({ items });
	};

	handleTailItemsChange = items => {
		const { tailSpendItems } = this.state;
		let updatedItems = [];
		// If item is not in tailSpendItems array then it is unchecked. Default is checked
		const isAllSelected = items.every(
			item =>
				!tailSpendItems.some(
					tailItem =>
						tailItem.name === item.name && tailItem.groupName === item.groupName
				)
		);

		if (isAllSelected) {
			updatedItems = [...tailSpendItems, ...items];
		} else {
			updatedItems = [...tailSpendItems];

			items.forEach(item => {
				const currentIndex = updatedItems.findIndex(
					tailItem =>
						item.name === tailItem.name && item.groupName === tailItem.groupName
				);

				if (currentIndex !== -1) {
					updatedItems = [
						...updatedItems.slice(0, currentIndex),
						...updatedItems.slice(currentIndex + 1),
					];
				}
			});
		}

		this.setState({ tailSpendItems: updatedItems });
	};

	render() {
		const {
			data,
			companies,
			groupBy,
			onGroupByChange,
			filterCategory,
			categoryOptions,
			onFilterCategoryChange,
			spendFormatter,
			initiativeData,
			isCommonVendors,
			onCommonVendorsChange,
			isLoading,
			onShowSegmentsChange,
			showSegments,
			showTopVendors,
		} = this.props;
		const {
			initiativeItemType,
			items,
			tailSegmentsConfiguration,
		} = initiativeData;
		const { spendRange, unit, minMax, searchItem } = this.state;
		const segments =
			tailSegmentsConfiguration && tailSegmentsConfiguration.segments;

		if (!data) {
			return (
				<LoadingContainer>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</LoadingContainer>
			);
		}

		const filteredData = this.getFilteredData();
		return (
			<Wrapper>
				<Header>
					<SpendWrapper>
						<InputWrapper>
							<ApInput
								symbol={unit.toUpperCase()}
								value={String(spendRange[0])}
								onChange={this.handleRangeLowerChange}
								type="number"
							/>
						</InputWrapper>
						<RangeWrapper>
							<Slider
								range
								value={spendRange}
								min={minMax[0]}
								max={minMax[1]}
								allowCross={false}
								onChange={this.handleSpendRangeChange}
							/>
						</RangeWrapper>
						<InputWrapper>
							<ApInput
								symbol={unit.toUpperCase()}
								value={String(spendRange[1])}
								onChange={this.handleRangeUpperChange}
								type="number"
							/>
						</InputWrapper>
					</SpendWrapper>

					<RightSection>
						{initiativeItemType === ITEMS_TYPE.VENDOR && (
							<React.Fragment>
								<ApToggleSwitch
									checked={isCommonVendors}
									onChange={onCommonVendorsChange}
								>
									Common vendors
								</ApToggleSwitch>
								<Divider />
							</React.Fragment>
						)}

						<ApSimpleDropdown
							options={GROUP_OPTIONS}
							selected={groupBy}
							onChange={onGroupByChange}
						/>
						<DropdownWrapper>
							<ApSimpleDropdown
								selected={filterCategory}
								options={categoryOptions}
								onChange={onFilterCategoryChange}
							/>
						</DropdownWrapper>
					</RightSection>
				</Header>

				{isLoading ? (
					<LoadingContainer>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoadingContainer>
				) : filteredData && filteredData.length > 0 ? (
					<ItemsTable
						data={filteredData}
						itemsType={initiativeItemType}
						companies={companies}
						items={items}
						spendFormatter={spendFormatter}
						onItemsChange={this.handleItemsChange}
						onTailItemsChange={this.handleTailItemsChange}
						highlightId={searchItem ? searchItem.id : null}
						showSegments={showSegments}
						segments={segments}
						showTopVendors={showTopVendors}
					/>
				) : (
					<NoData>No data available</NoData>
				)}

				<AbsoluteWrapper>
					<SearchWrapper>
						<SearchInput
							options={this.getSearchOptions(filteredData)}
							onChange={this.selectSearchItem}
							placeholder="Search"
							placement="top-start"
							charCount={2}
							showIcon
							isDismissible
						/>
					</SearchWrapper>
					{initiativeItemType === ITEMS_TYPE.TAIL_SPEND && (
						<ApToggleSwitch
							checked={showSegments}
							onChange={onShowSegmentsChange}
						>
							Show segments
						</ApToggleSwitch>
					)}
				</AbsoluteWrapper>
			</Wrapper>
		);
	}
}

export default ItemsSelectionStep;
